/**
 * CheckoutPage Component
 */

import { requestPlaceOrder, requestSetCheckoutFromLocalStorage } from '../../../redux/actions/checkout';
import { requestFetchCountries } from '../../../redux/actions/countries';
import { setHeaderActions, setHeaderType } from '../../../redux/actions/ui';
import { getBillingAddress } from '../../../redux/form/selectors';
import { isActiveStep, isReadyToPlaceOrder } from '../../../redux/helpers/checkout';
import { checkoutFragment, CheckoutPageQuery } from '../../../relay/checkout';
import { checkoutLicenseLineItemsAreValid } from '../../../relay/selectors/lineItem';
import { emptyPromiseActionPayload } from '../../../utils/promise';
import { CheckoutStepId } from './config';
import './styles.scss';

import * as classNames from 'classnames';
import { RouteMatch } from 'found';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createRefetchContainer } from 'react-relay';
import ScriptLoader from 'react-script-loader-hoc';
import { Elements, injectStripe, ReactStripeElements, StripeProvider } from 'react-stripe-elements';
import * as WindowSizeListener from 'react-window-size-listener';
import { Dispatch } from 'redux';
import { VelocityComponent, VelocityTransitionGroup } from 'velocity-react';

import Page from '../../../containers/Page';
import { pushNotification } from '../../../redux/actions/notifications';
import Button from '../../atoms/buttons/Button';
import LinkButton from '../../atoms/buttons/LinkButton';
import LoadingSpinner from '../../atoms/feedback/LoadingSpinner';
import Heading from '../../atoms/text/Heading';
import PageHeader from '../../molecules/blocks/PageHeader';
import UserProfile from '../../organisms/global/AppNavigation/UserProfile';
import { CartPagePath } from '../Cart';
import { CheckoutCompletePagePath } from '../CheckoutComplete';
import CheckoutPromoCodeForm from './CheckoutPromoCode';
import CheckoutSteps from './CheckoutSteps';
import CheckoutSummary from './CheckoutSummary';

// Page Path
export const CheckoutPagePath = '/my/checkout';

// Page Props
interface IOwnStateProps {
  billingAddress?: IAddress;
  readyToPlaceOrder: boolean;
  userIsAtReviewStep: boolean;
}

interface IOwnDispatchProps {
  dispatch: Dispatch<any>;
  fetchCountries(): void;
  initHeader(): void;
  placeOrder(): Promise<any>;
  resetHeader(): void;
  retrieveFromLocalStorage(): void;
}

interface IOwnProps extends ReactStripeElements.InjectedStripeProps {
  relay: IRelayProps<ICheckoutQueryVariables>;
  viewer: GQL.IViewer;
}

type CheckoutPageProps = IOwnProps & IOwnStateProps & IOwnDispatchProps & RouteMatch;

interface IOwnState {
  inReviewMargin: number;
  inReviewWidth: number;
  redirecting: boolean;
  shouldHideSidebarOnReview: boolean;
}

const classes = new BEM('checkout');
const defaultList = [];

class CheckoutPage extends React.Component<CheckoutPageProps, IOwnState> {

  constructor(props: CheckoutPageProps) {
    super(props);

    this.state = {
      inReviewMargin: 0,
      inReviewWidth: 0,
      redirecting: !checkoutLicenseLineItemsAreValid(props.viewer),
      shouldHideSidebarOnReview: false,
    };
  }

  componentDidMount() {
    this.props.fetchCountries();
    this.props.initHeader();
    this.props.retrieveFromLocalStorage();

    if (this.state.redirecting) {
      this.props.router.replace(CartPagePath);
    }
  }

  componentWillReceiveProps(nextProps: CheckoutPageProps) {
    if (nextProps.userIsAtReviewStep && !this.props.userIsAtReviewStep) {
      this.onReview();
    }
  }

  componentDidUpdate(prevProps: CheckoutPageProps) {
    if (!prevProps.readyToPlaceOrder && this.props.readyToPlaceOrder) {
      this.onPlaceOrder();
    }
  }

  componentWillUnmount() {
    this.props.resetHeader();
  }

  async onPlaceOrder() {
    try {
      await this.props.placeOrder();
      const { invoice_number } = this.props.viewer.checkout!;
      this.props.router.push(`${CheckoutCompletePagePath}?order=${invoice_number}`);
    } catch (error) {
      this.props.dispatch(pushNotification({
        autoDismiss: false,
        // tslint:disable-next-line:max-line-length
        message: 'Something went wrong while placing your order. Please contact us at +1 888-423-6665 (NA) or +33 1 70 38 73 50 (EU) so we can complete your order.',
        state: 'error',
      }));
    }
  }

  onReview() {
    const { billingAddress, relay } = this.props;

    const refetchVariables: ICheckoutQueryVariables = {
      country_code: billingAddress!.country,
      subdivision_code: billingAddress!.state,
    };

    relay.refetch(refetchVariables, null, null, { force: true });
  }

  onResize = ({ windowWidth }) => {
    const viewportWidth = window.innerWidth - 48;
    let inReviewWidth = viewportWidth < 1152 ? viewportWidth : viewportWidth * .9;
    if (inReviewWidth > 1152) {
      inReviewWidth = 1152;
    }
    const inReviewMargin = (viewportWidth - inReviewWidth) / 2;
    this.setState({ inReviewMargin, inReviewWidth });
  }

  renderSteps() {
    const {
      userIsAtReviewStep,
      viewer: {
        checkout,
        company_types,
        representative_roles,
      },
    } = this.props;
    const shouldHideSidebarOnReview = this.state.shouldHideSidebarOnReview && userIsAtReviewStep;
    return (
      <VelocityComponent
        animation={{
          translateX: shouldHideSidebarOnReview ? this.state.inReviewMargin : 0,
          minWidth: shouldHideSidebarOnReview ? this.state.inReviewWidth : 0,
        }}
        delay={shouldHideSidebarOnReview ? 150 : 0}
        duration={250}
      >
        <CheckoutSteps
          companyTypes={company_types!}
          order={checkout!}
          representativeRoles={representative_roles!}
        />
      </VelocityComponent>
    );
  }

  renderSidebar() {
    const { userIsAtReviewStep, viewer: { checkout } } = this.props;
    const shouldHideSidebarOnReview = this.state.shouldHideSidebarOnReview && userIsAtReviewStep;
    return (
      <VelocityComponent
        animation={{
          opacity: shouldHideSidebarOnReview ? 0 : 1,
          translateX: shouldHideSidebarOnReview ? 200 : 0,
        }}
        delay={shouldHideSidebarOnReview ? 0 : 150}
        duration={250}
        display={shouldHideSidebarOnReview ? 'none' : 'block'}
      >
        <aside {...classes('sidebar')}>
          <div {...classes('help-box')}>
            <Heading is="h4">Need Help?</Heading>
            <Heading is="h5">Call us toll free at +1 888-423-6665 (NA) / +33 1 70 38 73 50 (EU)</Heading>
            <Button
              className="sample-license-button"
              hyperLinkProps={{
                href: 'https://assets.boppermusic.com/docs/Bopper_Sample_License.pdf',
                target: '_blank',
              }}
              wrapperComponent="a"
              colorScheme="light"
              variant="pill"
              size="small"
              label="Preview License"
            />
          </div>
          <CheckoutSummary order={checkout!} />
          <CheckoutPromoCodeForm />
        </aside>
      </VelocityComponent>
    );
  }

  render() {
    const shouldHideSidebarOnReview = this.state.shouldHideSidebarOnReview && this.props.userIsAtReviewStep;
    const extraClasses = classNames({ 'review-step': shouldHideSidebarOnReview });
    return (
      <Page id="checkout" className={extraClasses}>
        {!this.state.redirecting && <>
          {!this.props.readyToPlaceOrder &&
          <PageHeader title="Checkout" withCurrencySelect />
        }
          <VelocityTransitionGroup
            component="div"
            {...classes('content-wrapper')}
            enter={{ animation: 'transition.expandIn', duration: 1000, delay: 750, display: 'flex' }}
          >
            {this.props.readyToPlaceOrder ? (
              <div key="checkout-processing" {...classes('content', 'processing')}>
                <LoadingSpinner />
                <Heading is="h3" {...classes('title')}>Thank you for your business!</Heading>
                <Heading is="h4" {...classes('subtitle')}>Processing your license...</Heading>
              </div>
            ) : (
                <div key="checkout-form" {...classes('content')}>
                  {this.renderSteps()}
                  {this.renderSidebar()}
                </div>
              )}
          </VelocityTransitionGroup>
        </>}
        <WindowSizeListener onResize={this.onResize} />
      </Page>
    );
  }
}

const ConnectedCheckoutPage = connect<IOwnStateProps, IOwnDispatchProps, CheckoutPageProps>(
  (state: IStoreState) => ({
    billingAddress: getBillingAddress(state),
    readyToPlaceOrder: isReadyToPlaceOrder(state),
    userIsAtReviewStep: isActiveStep(CheckoutStepId.review)(state),
  }),
  (dispatch, ownProps) => ({
    dispatch,
    fetchCountries: () => dispatch(requestFetchCountries(emptyPromiseActionPayload)),
    initHeader: () => {
      dispatch(setHeaderType({ type: 'simple' }));
      dispatch(setHeaderActions({
        actions: [(
          <LinkButton
            to={CartPagePath}
            label="Back"
            size="small-medium"
          />
        ), (
          <UserProfile profile={ownProps.viewer.profile} />
        )],
      }));
    },
    placeOrder: () => new Promise((resolve, reject) => {
      dispatch(requestPlaceOrder({ promise: { resolve, reject } }));
    }),
    resetHeader: () => {
      dispatch(setHeaderActions({ actions: defaultList }));
      dispatch(setHeaderType({ type: 'simple' }));
    },
    retrieveFromLocalStorage: () => dispatch(requestSetCheckoutFromLocalStorage()),
  }),
)(CheckoutPage);

const RelayCheckoutPage = createRefetchContainer(
  ConnectedCheckoutPage,
  checkoutFragment,
  CheckoutPageQuery,
);

type BasePageProps = Pick<CheckoutPageProps, 'viewer'>;
type CheckoutWithStripeProps = BasePageProps & { scriptsLoadedSuccessfully: boolean };

const CheckoutPageWithStripe =
  injectStripe(RelayCheckoutPage) as React.ComponentClass<BasePageProps>;

function CheckoutWithStripe({ scriptsLoadedSuccessfully, ...props }: CheckoutWithStripeProps) {
  return scriptsLoadedSuccessfully ? (
    <StripeProvider apiKey={props.viewer.stripe!.public_key}>
      <Elements>
        <CheckoutPageWithStripe {...props} />
      </Elements>
    </StripeProvider>
  ) : null;
}

const CheckoutWithStripeLoader = ScriptLoader('https://js.stripe.com/v3/')(CheckoutWithStripe);

export default CheckoutWithStripeLoader;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Checkout/index.tsx